"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useVoiceStore, currentAudioLevel } from "@/stores/voice-store";

const COLOR = "#FFAF53";
const CORNER_R = 10;

const LAYERS = [
  { baseSw: 4, maxSw: 50, blur: 65, baseOp: 0.08, maxOp: 0.22, color: COLOR },
  { baseSw: 3, maxSw: 28, blur: 45, baseOp: 0.12, maxOp: 0.38, color: COLOR },
  { baseSw: 2, maxSw: 14, blur: 30, baseOp: 0.18, maxOp: 0.55, color: COLOR },
  { baseSw: 1.5, maxSw: 4, blur: 3, baseOp: 0.4, maxOp: 0.9, color: COLOR },
  { baseSw: 0.5, maxSw: 1.5, blur: 1, baseOp: 0.1, maxOp: 0.35, color: "#fff" },
];

function ActiveGlow() {
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const refs = useRef<(SVGRectElement | null)[]>(
    Array<SVGRectElement | null>(LAYERS.length).fill(null),
  );
  const smoothed = useRef(0);

  useEffect(() => {
    const sync = () =>
      setDims({ w: window.innerWidth, h: window.innerHeight });
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  useEffect(() => {
    if (!dims.w) return;
    let raf: number;

    const tick = () => {
      smoothed.current = smoothed.current * 0.92 + currentAudioLevel * 0.08;
      const level = smoothed.current;

      for (let i = 0; i < LAYERS.length; i++) {
        const el = refs.current[i];
        if (!el) continue;
        const l = LAYERS[i];
        const sw = l.baseSw + (l.maxSw - l.baseSw) * level;
        const op = l.baseOp + (l.maxOp - l.baseOp) * level;
        el.setAttribute("stroke-width", String(sw));
        el.setAttribute("opacity", String(op));
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dims]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 9999 }}
    >
      {dims.w > 0 && (
        <svg
          width={dims.w}
          height={dims.h}
          className="absolute inset-0"
        >
          {LAYERS.map((layer, i) => (
            <rect
              key={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              x={0}
              y={0}
              width={dims.w}
              height={dims.h}
              rx={CORNER_R}
              ry={CORNER_R}
              fill="none"
              stroke={layer.color}
              strokeWidth={layer.baseSw}
              style={{ filter: `blur(${layer.blur}px)` }}
              opacity={layer.baseOp}
            />
          ))}
        </svg>
      )}
    </motion.div>
  );
}

export function VoiceGlowOverlay() {
  const isActive = useVoiceStore((s) => s.isVoiceActive);
  return (
    <AnimatePresence>
      {isActive && <ActiveGlow key="voice-glow" />}
    </AnimatePresence>
  );
}
